import { AxiosError, AxiosResponse } from 'axios'
import { isPast, minutesToMilliseconds, secondsToMilliseconds } from 'date-fns'
import { memoize } from 'lodash-es'
import { Store } from 'redux'
import { SetRequired } from 'type-fest'
import { z } from 'zod'

import { appConfig } from '@/app-config/appConfig'
import { asyncWait } from '@/lib/asyncWait'
import { createGetGoingAuthFailedResponseInterceptor, createGetGoingAuthRequestInterceptor } from '@/lib/auth'
import { captureAndLogError } from '@/lib/error'
import { IAction } from '@/redux/actions/action-helpers'
import { ApplicationState } from '@/redux/stores/types'
import { AppError, ErrorCode, isAbortError } from '@/types/error'
import { GGClientError } from '@/types/error/GGClientError'
import { CreateJobResponseZ, getJobResultResponseZ, JobResultResponse, JobStatus, WithJobId } from '@/types/job'

import { BaseApiClient, BaseRequestConfig, EnforceRelativeURL, RequestOverrides } from '../BaseApiClient'

export type { RequestOverrides } from '../BaseApiClient'

const POLL_ACTION = Symbol()

export type JobRequestOverrides = {
	signal?: AbortSignal
	create?: Omit<RequestOverrides, 'signal'>
	poll?: Omit<RequestOverrides, 'signal'>
}

export type JobResultMapper<T, R> = (job: AxiosResponse<T>, poll: typeof POLL_ACTION) => R | typeof POLL_ACTION

const CommonJobResponseSchemaZ = getJobResultResponseZ(z.any())

export function mapJobResult<T>(
	response: AxiosResponse<JobResultResponse<T>>,
	poll: typeof POLL_ACTION,
): T | typeof POLL_ACTION {
	const validation = CommonJobResponseSchemaZ.safeParse(response.data)
	if (validation.success) {
		const jobStatus = validation.data.status
		if (jobStatus === JobStatus.Completed) {
			return validation.data.result as T
		}
		if (jobStatus === JobStatus.Failed) {
			return GGClientError.matchApiError({
				_: ErrorCode.not_mapped,
			})(validation.data.error)
		}
	} else {
		captureAndLogError(validation.error, {
			level: 'warning',
			extra: {
				source: 'api_error_validation',
			},
		})
		throw new GGClientError({
			code: ErrorCode.invalid_response,
			message: `Job failed, but the error response field has invalid format`,
			reason: 'Auto mapped because the error response field has invalid format',
			cause: validation.error,
			extra: { responseData: response.data },
		})
	}
	return poll
}

const getCachedJobSchema = memoize(<S extends z.ZodTypeAny>(s: S) => getJobResultResponseZ(s))

/** Wraps different kinds of abortion/cancellation/timeout errors into an `AppError` with `ErrorCode.cancelled` */
export function createAbortErrorWrapperResponseInterceptor() {
	return function abortErrorWrapperResponseInterceptor(error: unknown) {
		throw isAbortError(error)
			? AppError.ensure(
					error,
					(e) =>
						new AppError({
							code: ErrorCode.cancelled,
							cause: e,
						}),
				)
			: error
	}
}

/**
 * Wraps Axios errors into a `GGClientError`, so it would be easier to process down the line.
 * Some default response status codes mapped to a corresponding `ErrorCode`.
 * Everything else is `ErrorCode.not_mapped`.
 */
export function createGGClientErrorResponseInterceptor() {
	const rootMapper = GGClientError.matchAxiosError({
		authentication_error: ErrorCode.not_authenticated,
		cancelled: ErrorCode.cancelled,
		error: ErrorCode.server_error,
		method_not_allowed: ErrorCode.misconfiguration,
		permission_denied: ErrorCode.not_authorized,
		timeout: ErrorCode.timeout,
	})
	return function ggClientErrorResponseInterceptor(error: AxiosError) {
		return rootMapper(error)
	}
}

class GetGoingClient extends BaseApiClient {
	private store: Store<ApplicationState, IAction>

	constructor() {
		super(appConfig.API_DOMAIN)

		this.client.interceptors.request.use(createGetGoingAuthRequestInterceptor())

		this.client.interceptors.response.use(
			undefined,
			createGetGoingAuthFailedResponseInterceptor(this.getStore, this.client),
		)
		this.client.interceptors.response.use(undefined, createAbortErrorWrapperResponseInterceptor())
		this.client.interceptors.response.use(undefined, createGGClientErrorResponseInterceptor())
	}

	public setStore(store: Store<ApplicationState, IAction>) {
		this.store = store
	}

	private getStore() {
		return this.store
	}

	/**
	 * Only polls. You need to create a job first by yourself.
	 * Defaults:
	 * - pollInterval = 2 seconds
	 * - pollTime = 5 minutes
	 * Together they give 5 mins of polling until an error.
	 */
	async pollJobResult<URL extends string, ResZ extends z.ZodTypeAny, R>(
		url: EnforceRelativeURL<URL>,
		config: Omit<SetRequired<BaseRequestConfig<z.ZodType<void>, ResZ>, 'responseSchema'>, 'params'> & {
			mapJobResult: JobResultMapper<z.infer<ResZ>, R>
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			params?: { [x: string]: any } & WithJobId
			pollInterval?: number
			pollTime?: number
		},
	) {
		const {
			mapJobResult: mapResult,
			pollInterval = secondsToMilliseconds(2),
			pollTime = minutesToMilliseconds(5),
			...reqConfig
		} = config
		const getResult = () => this.request('GET', url, reqConfig).then((job) => mapResult(job, POLL_ACTION))
		const endTime = Date.now() + pollTime

		let result = await getResult()

		while (result === POLL_ACTION) {
			if (isPast(endTime)) {
				throw GGClientError.from(ErrorCode.cancelled, 'Polling time limit has been reached')
			}

			await asyncWait(pollInterval)
			result = await getResult()
		}

		return result
	}

	async simplePollingRequest<URL extends string, ReqZ extends z.ZodTypeAny, ResZ extends z.ZodTypeAny>(
		url: EnforceRelativeURL<URL>,
		config: Omit<BaseRequestConfig<ReqZ, ResZ>, 'responseSchema'> & {
			resultSchema: ResZ
			pollInterval?: number
			pollTime?: number
		},
	) {
		const { data, params, requestSchema, resultSchema, ...sharedConfig } = config

		const { job_id } = await getGGClient()
			.request('POST', url, {
				requestSchema,
				responseSchema: CreateJobResponseZ,
				data,
				params,
				...sharedConfig,
			})
			.then((r) => r.data)

		const jobResponseSchema = getCachedJobSchema(resultSchema)

		return getGGClient().pollJobResult<URL, typeof jobResponseSchema, z.infer<ResZ>>(url, {
			params: { ...params, job_id },
			responseSchema: jobResponseSchema,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			mapJobResult: mapJobResult<any>,
			...sharedConfig,
		})
	}
}

let client: GetGoingClient

export function getGGClient() {
	if (!client) {
		client = new GetGoingClient()
	}
	return client
}
